import React from "react";
import { SurveyReportDataSet, SurveyReportBuilder, rng, getSurveyStatus } from "../../../../../../amorg-common";
import { ActionPlanListView } from "../../../../../../amorg-common/DTOs/ActionPlanListView";
import { getActionPlansByActivityId } from "../../../../../../api/actionPlanClientApi";
import { updateActivity } from "../../../../../../api/activityClientApi";
import { createSurvey } from "../../../../../../api/surveyClientApi";
import { ActionCardContainer, ActionCard } from "../../../../../../designSystem/ActionCard";
import { TextAvatar } from "../../../../../../designSystem/avatar/TextAvatar";
import { Button } from "../../../../../../designSystem/button/Button";
import { CollapsiblePanel } from "../../../../../../designSystem/collapsible/CollapsiblePanel";
import { colors } from "../../../../../../designSystem/colors/color";
import { Flex } from "../../../../../../designSystem/compositeComponents/FlexHelpers";
import { LinkWithCopyButton } from "../../../../../../designSystem/compositeComponents/LinkWithCopyButton";
import { DownloadableQrCode } from "../../../../../../designSystem/DownloadableQrCode";
import { Icon } from "../../../../../../designSystem/icons/Icons";
import { LoadingPageOverlay } from "../../../../../../designSystem/LoadingPageOverlay";
import { MessageCard } from "../../../../../../designSystem/Message";
import { Small } from "../../../../../../designSystem/typography/Body";
import { ActivityDetailsPanel } from "../../../../../_components/activities/ActivityDetailsPanel";
import { ActivityHeader } from "../../../../../_components/activities/ActivityHeader";
import { timeToOpen, timeToClose } from "../../../../../../utils/date";
import { ReportSummarySection } from "../_components/ReportSummarySection";
import { SurveyActionPlanStatusSection } from "../_components/SurveyActionPlanStatusSection";
import { WorkplaceAssessmentComponentProps } from "../_components/WorkplaceAssessmentComponentProps";
import { TitleContainer } from "../../../../../../designSystem/compositeComponents/TitleContainer";
import { IconWithText } from "../../../../../../designSystem/compositeComponents/compositeIcons/IconWithText";
import { Em } from "../../../../../../designSystem/typography/Em";



type State = {
    data:SurveyReportDataSet | null,
    actionPlans: ActionPlanListView[]
}
export function OverviewPanel(props:WorkplaceAssessmentComponentProps): JSX.Element {
    const [state, setState] = React.useState<State>(
        {
            data: null,
            actionPlans: []
        });

    React.useEffect(() => {
        const getData = async () => {
            if(props.surveyLoading){
                setState({data:null, actionPlans:[]})
            }
            else if(props.survey && props.surveyReport) {
                const builder = new SurveyReportBuilder(props.survey, props.surveyReport);
                setState({
                    data:builder.getData(),
                    actionPlans:await getActionPlansByActivityId(props.survey.activityId)
                })
            }
        };
        getData();
        
    }, [props.survey, props.surveyReport, props.surveyLoading]);

    async function handleClickCreateSurvey() {
        const id = rng.getRandomId();
        const result = await createSurvey(id, props.activity.id);
        if (result.isSuccess) {
            props.onSurveyUpdated();
        }
    }

    async function updateTitle(value: string) {
        await updateActivity(props.activity.id, value, props.activity.description, props.activity.start, props.activity.end, props.activity.location);
        props.onActivityUpdated();
    }

    let openStatus:'waiting' | 'open' | 'closed' = 'waiting';
    if(props.survey)
    {
        if(props.survey.start > new Date())
            openStatus = 'waiting';
        else {
            if(props.survey.end >= new Date())
                openStatus = 'open';
            else
                openStatus = 'closed';
        }
    }

    const canSubmitSurveyAnswer = props.survey ? getSurveyStatus(props.survey).canSubmitAnswer : false;
    const surveyAnswerUrl = `${window.location.protocol}//${window.location.host}/surveys/${props.survey?.id || ''}@${props.activeUser.siteId}`;

    return (
        <>
            <LoadingPageOverlay loading={!props.activity || props.surveyLoading} />

            <ActivityHeader
                activity={props.activity}
                deleteConfirmationTextInModal="Tilhørende handleplaner, spørgeskemaer samt besvarelser bliver også slettet." />

            <Flex flexDirection='column'>
                    
                <CollapsiblePanel 
                    defaultOpen={true}
                    openHeader={
                        <TitleContainer title={props.activity.title} updateTitle={updateTitle} /> 
                    }
                    closedOrDefaultHeader={
                        <TitleContainer title={props.activity.title} updateTitle={updateTitle} /> 
                }>
                        
                    <ActivityDetailsPanel
                        componentTitle="APV Info"
                        periodSelection="date"
                        activity={props.activity}
                        descriptionText="APV beskrivelse"
                        onActivityUpdated={props.onActivityUpdated} 
                        users={props.users}
                        />    
                    
                </CollapsiblePanel>
                        
            </Flex>

            {props.activity &&
                <>
                    
                    <Flex flexDirection="column" gap={16}>
                        {props.survey &&
                            
                            <ActionCardContainer minCardWidth="240px" gap={16} style={{ marginTop: 20 }}>

                                <ActionCard header="Status" style={{ flexGrow: 1, minWidth: '140px' }}>

                                    <Flex flexDirection="column" gap={8}>

                                    {openStatus === 'waiting' &&
                                        <>   
                                        <IconWithText icon="interface_glyph_hourglass" variant="passive">
                                            <span>Spørgeskemaet åbner snart</span>
                                        </IconWithText>

                                        <IconWithText icon="clock" variant="warning">
                                            <span>{timeToOpen(props.survey.start, new Date())}</span>
                                        </IconWithText>
                                        </>
                                    }

                                    {openStatus === 'open' &&
                                        <>
                                        <IconWithText icon="lock_open" variant="success">
                                            <span>Spørgeskemaet er åbent</span>
                                        </IconWithText>
                                        <IconWithText icon="clock" variant="warning">
                                            <span>{timeToClose(props.survey.end, new Date())}</span>
                                        </IconWithText>
                                        </>
                                    }

                                    {openStatus === 'closed' &&
                                        <>
                                        <IconWithText icon="lock" variant="error">
                                            <span>Spørgeskemaet er lukket</span>
                                        </IconWithText>
                                        </>
                                    }
                                    </Flex>

                                </ActionCard>


                                <ActionCard header="Undersøgelsen" style={{ flexGrow: 1, minWidth: '140px' }}>
                                    <Flex flexDirection="column" gap={8} alignItems="flex-start">
                                        <Flex gap={4}>
                                            <TextAvatar text={props.survey.themes.flatMap(t => t.questions).length.toString()} backgroundColor={colors.avatar.background} /> <span>spørgsmål fordelt på </span>
                                        </Flex>
                                        <Flex gap={4}>
                                            <TextAvatar text={props.survey.themes.length.toString()} backgroundColor={colors.avatar.backgroundLight} /> <span>Temaer</span>
                                        </Flex>
                                    </Flex>

                                </ActionCard>


                                <ActionCard header="Besvarelser" style={{ flexGrow: 1, minWidth: '140px' }}>
                                    <Flex flexDirection="column" gap={8}>
                                        <Flex gap={4}>
                                            <Em>{props.survey.answerCount.toString()}</Em> <span>oprettet</span>
                                        </Flex>

                                        {props.survey.answerCount > 0 &&

                                            <>
                                                {props.survey.finishedAnswerCount == props.survey.answerCount &&
                                                    <span>Alle afsluttet</span>
                                                }

                                                {props.survey.finishedAnswerCount < props.survey.answerCount &&
                                                    <Flex gap={4}>
                                                        <Em error>{props.survey.answerCount - props.survey.finishedAnswerCount}</Em> <span>besvarelser ufærdige</span>
                                                    </Flex>
                                                }
                                            </>
                                        }
                                    </Flex>
                                </ActionCard>


                                <ActionCard header="Oprettelse af besvarelse" style={{ flexGrow: 1, minWidth: '140px' }}>
                                    <Flex flexDirection="column" gap={4}>
                                        <LinkWithCopyButton
                                            disabled={!canSubmitSurveyAnswer}
                                            url={surveyAnswerUrl}
                                        />
                                        
                                        <Flex justifyContent="center">
                                            <DownloadableQrCode
                                                disabled={!canSubmitSurveyAnswer}
                                                url={surveyAnswerUrl}
                                                fileName="qr-kode-spoergeskema.svg"
                                            />
                                        </Flex>
                                    </Flex>
                                </ActionCard>

                            </ActionCardContainer>
                        }
                        
                        {!props.survey &&
                            <>
                            <MessageCard variant="info">
                                Der er ikke oprettet et spørgeskema for APVen.
                            </MessageCard>
                            
                            <Button
                                variant='light'
                                size={"42px"}
                                onClick={handleClickCreateSurvey}
                                text={"Opret spørgeskema"} 
                                icon="actions_plus"
                                />
                            </>
                        }
                    </Flex>

                    {props.survey && props.surveyReport && state.data &&
                        <>
                            
                            <ReportSummarySection survey={props.survey} data={state.data} excludeQuestionSummary />

                            <SurveyActionPlanStatusSection headerText="Status for oprettelse af handleplaner" survey={props.survey} surveyReport={props.surveyReport} actionPlans={state.actionPlans} />
                        </>
                    }
                </>
            }
        </>
    );
}